import mParticle, {
  type MPConfig,
  type SDKEventAttrs,
  type SDKEventCustomFlags,
} from '@mparticle/web-sdk'
import appBoyKit from '@mparticle/web-braze-kit'
import * as braze from '@braze/web-sdk'

import pubsub from '@/lib/pubsub'
import { httpPostJson } from '@/lib/net'
import browser from '@/lib/browser'
import { productSkuNames } from '@/lib/plans'
import device from '@/lib/device'
import type { Environment } from '@/types/types'
import type { TransactionMarketingDetails } from '@/types/purchasing'
import { kochavaLoaded } from '@/types/kochava-sdk'
import { log, sendUserIdToFirebase } from '@/services/logging-service'
import gtm from '@/lib/gtm'
import { info } from '@/lib/logger'

const hasStates = (states?: string[]): states is string[] => {
  return !!states?.length
}

type mParticleEventData = Record<
  string,
  Exclude<SDKEventAttrs[keyof SDKEventAttrs], boolean>
>

// create a wrapper type here so we don't have to import directly mparticle all over the codebase
export type EventProps = SDKEventAttrs

/**
 * Library for sending out marketing utility events.
 * Any new events need to follow the guidelines and instructions listed
 * here: https://github.com/onXmaps/webmap-onxmaps/blob/develop/CONTRIBUTING.md#marketing-events
 */
class Marketing {
  marketingEnabled: boolean
  mPIdentifyRequest: mParticle.IdentifyRequest
  environment?: Environment
  private _initialized = false

  constructor() {
    /**
     * If prebid-ads.js is loaded, then it sets a global window variable that marketing is enabled.
     *
     * This idea is taken from https://stackoverflow.com/a/20505898/8894424
     * Most ad blockers will not load urls with "ad" or "prebid" in them. So if this file is blocked
     * we can either suppress or change our marketing events.
     */
    this.marketingEnabled = window.marketingEnabled ?? false
    this.mPIdentifyRequest = {
      userIdentities: {},
    }
  }

  initialize(environment: Environment) {
    if (this._initialized) {
      // If already is initialized, then skip
      return
    }
    if (!environment?.apiUrl) {
      return
    }
    this.environment = environment

    // navigation events
    pubsub.subscribe(
      'updateMapModeUserProperty',
      this.updateMapModeUserProperty.bind(this)
    )

    // account events
    pubsub.subscribe('logoutRequestCompleted', this.logout.bind(this))

    // help and settings
    pubsub.subscribe('changedUnits', this.changedUnits.bind(this))

    // markup events
    pubsub.subscribe('markupUndo', this.markupUndoClicked.bind(this))

    // update properties
    pubsub.subscribe(
      'updateCollectionCountUserProperty',
      this.updateCollectionCountUserProperty.bind(this)
    )
    pubsub.subscribe(
      'updateWaypointCountUserProperty',
      this.updateWaypointCountUserProperty.bind(this)
    )
    pubsub.subscribe(
      'updateWaypointWindCountUserProperty',
      this.updateWaypointWindCountUserProperty.bind(this)
    )

    // initialize
    pubsub.subscribe(
      'newAccountRequestCompleted',
      this.accountCreated.bind(this)
    )

    // Web components events
    pubsub.subscribe('x-marketing-event', this.logVueComponentEvent.bind(this))

    gtm.initialize()
    this.loadBraze()
    this.loadMParticle()
    this._initialized = true
  }

  /**
   * Methods to fire after we have a user
   */
  // eslint-disable-next-line @typescript-eslint/require-await, require-await
  async afterLogin() {
    gtm.setUserEmail()
    this.mParticleLogin()
    if (mParticle.isInitialized()) {
      // Manually trigger batch upload to avoid sending events without a User ID
      mParticle.upload()
    }
  }

  /**
   * Log an mParticle event through the analytics router
   *
   * @return Promise that resolves to the result of the request
   */
  // eslint-disable-next-line require-await
  async logArMparticleEvent(
    /** Event name */
    name: string,
    /** Properties to send to mParticle */
    props: mParticleEventData = {}
  ) {
    const body = {
      event_name: name,
      event_params: props,
    }

    return httpPostJson('/v1/analytics/mparticle', body, {
      /**
       * Allows the request to still be sent after the page is closed.
       * @see https://developer.mozilla.org/en-US/docs/Web/API/fetch#keepalive
       */
      keepalive: true,
    }).catch(() => {})
  }

  /**
   * Log an mParticle event through the SDK
   */
  logSdkMparticleEvent(
    /** Event name */
    name: string,
    /** Properties to send to mParticle */
    props: mParticleEventData = {},
    /** Custom flags to send to mParticle */
    customFlags: SDKEventCustomFlags = {}
  ) {
    // mP requires an event type for custom events. I defined all events
    // under the type "other" so that event sorting can be done within mP and not
    // client-side as that would be impossible to maintain given how often we add
    // and edit events and the number of types they have defined.
    const type = mParticle.EventType.Other
    return mParticle.logEvent(name, type, props, customFlags)
  }

  /**
   * Log an mParticle event either through the SDK (if ad blocking is disabled) or the
   * Analytics Router (if ad blocking is enabled).
   *
   * @return Promise that resolves after the fetch call resolves
   */
  logMparticleEvent(
    /**  Event name */
    name: string,
    /** Properties to send to mParticle */
    props: SDKEventAttrs = {},
    /** Custom flags to send to mParticle */
    customFlags: SDKEventCustomFlags = {}
  ) {
    const sanitizedData = this.sanitizeMparticleEventData(props)
    return this.marketingEnabled
      ? this.logSdkMparticleEvent(name, sanitizedData, customFlags)
      : this.logArMparticleEvent(name, sanitizedData)
  }

  /**
   * Log a behavior event
   */
  logEvent(
    /** Event name */
    name: string,
    /** Properties to send to all SDKs */
    props?: SDKEventAttrs
  ) {
    info('Marketing', 'send event:', name, 'props:', props)
    this.logMparticleEvent(name, props)
  }

  /**
   * Log a Kochava event
   */
  logKochavaEvent(
    /** Event name */
    name: string,
    /**Properties to send to the kochava event */
    props: object = {}
  ) {
    if (kochavaLoaded(window.kochava)) {
      window.kochava.activity(name, props)
    }
  }

  /**
   * Initializes the Braze SDK
   */
  loadBraze() {
    if (this.environment) {
      const DEBUG = false
      const brazeSDKEndpoint = 'sdk.iad-06.braze.com'
      const key = this.environment.brazeFishKey

      const successfulInit = braze.initialize(key, {
        baseUrl: brazeSDKEndpoint,
        enableLogging: DEBUG,
      })

      if (successfulInit) {
        braze.openSession()
      }

      // Destroy the session on logout. Note: if the user doesn't log out, session
      // has a default set to close after 30min of inactivity.
      pubsub.subscribe('logoutRequestCompleted', () => braze.destroy())
    } else {
      log('Unable to initialize Braze - no environment provided')
    }
  }

  /**
   * Loads the mParticle script to page head to initialize the SDK
   */
  async loadMParticle() {
    if (this.environment) {
      const key = this.environment.mParticleFishKey

      const isDevelopmentMode = window.location.host !== 'fish.onxmaps.com'
      // Use same custom deviceId Split uses for mParticle so device id is consistent between Split and mParticle
      // This will allow anonymous split_impression events to be properly attributed to a user after log in
      device.checkDeviceID()
      const config: MPConfig = {
        deviceId: device.deviceID,
        isDevelopmentMode,
        v1SecureServiceUrl: 'api.mparticle.onxmaps.com/webevents/v1/JS/',
        v2SecureServiceUrl: 'api.mparticle.onxmaps.com/webevents/v2/JS/',
        v3SecureServiceUrl: 'api.mparticle.onxmaps.com/webevents/v3/JS/',
        configUrl: 'api.mparticle.onxmaps.com/tags/JS/v2/',
        identityUrl: 'api.mparticle.onxmaps.com/identity/v1/',
        aliasUrl: 'api.mparticle.onxmaps.com/webevents/v1/identity/',
        onCreateBatch: function (batch) {
          // the following removes Session Start and Session End events
          batch.events = batch.events?.filter((event) => {
            return (
              event.event_type !== 'session_start' &&
              event.event_type !== 'session_end'
            )
          })
          return batch
        },
      }
      const { hasValidUserSession, getUserId, getUserProfile } =
        await import('@/services/identity-client')
      const [validSession, userId, profile] = await Promise.all([
        hasValidUserSession(),
        getUserId(),
        getUserProfile(),
      ])

      // default to no identifyRequest if there's no token
      if (validSession) {
        const email = profile?.email || ''
        const id = userId

        this.mPIdentifyRequest.userIdentities.customerid = id
        // Only send an email if one is available, so as not to assign an empty email
        // to the id. We won't have an email for someone who signed in with Facebook.
        if (email) {
          this.mPIdentifyRequest.userIdentities.email = email
        }
        config.identifyRequest = this.mPIdentifyRequest
      }

      appBoyKit.register(config)
      mParticle.init(key, config)

      // Note: if this is only called on actual user login, it does not update
      // the user identity config on the map page where we need to start recording
      // the token. So this will call on page load to ensure we're updating the
      // config properly based on logged in/logged out state for each page.
      if (validSession) {
        this.mParticleLogin()
      }
    } else {
      log('Unable to initialize mParticle - no environment provided')
    }
  }

  /**
   * Login identify request to mP
   */
  async mParticleLogin(attempts = 0) {
    const { hasValidUserSession, getUserId, getUserProfile } =
      await import('@/services/identity-client')
    const validSession = await hasValidUserSession()
    if (!validSession) {
      return
    }
    const [userId, profile] = await Promise.all([getUserId(), getUserProfile()])
    const email = profile?.email || ''
    const id = userId

    this.mPIdentifyRequest.userIdentities.customerid = id

    if (userId) {
      sendUserIdToFirebase(userId)
    }

    // Only send an email if one is available, so as not to assign an empty email
    // to the id. We won't have an email for someone who signed in with Facebook.
    if (email) {
      this.mPIdentifyRequest.userIdentities.email = email
    }

    mParticle.Identity.login(this.mPIdentifyRequest, (result) => {
      // if this callback returns anything other than 200, fall back to using analytics
      // router instead of the mParticle web sdk
      if (result.httpCode !== 200) {
        if (attempts === 3) {
          this.marketingEnabled = false
        } else {
          setTimeout(
            () => {
              this.mParticleLogin(attempts + 1)
            },
            2 ** (attempts + 1) * 100
          )
        }
      }
    })

    pubsub.subscribe('logoutRequestCompleted', () => {
      mParticle.Identity.logout({})
    })
  }

  /**
   * helper function to convert url to origin property
   */
  getOrigin(
    /** url user was directed from */
    prevURL: string
  ) {
    if (prevURL) {
      switch (true) {
        case prevURL.includes('create-account'):
          return 'create account'
        case prevURL.includes('login'):
          return 'login'
        case prevURL.includes('purchase'):
          return 'purchase flow'
        case prevURL.includes('map'):
          return 'logout'
        default:
          return 'app launch'
      }
    }
    return 'app launch'
  }

  /**
   * user clicks Login button and attempts to sign in
   */
  viewedLogin(
    /** url user was directed from */
    prevURL: string
  ) {
    const origin = this.getOrigin(prevURL)
    this.logEvent('login_screen_viewed', {
      origin,
      desktop: !browser.isMobile(),
    })
  }

  /**
   * user clicks the Create Account button and attempts to sign in
   */
  viewedCreateAccount(
    /** url user was directed from */
    prevURL: string
  ) {
    const origin = this.getOrigin(prevURL)
    this.logEvent('create_account_screen_viewed', { origin })
  }

  /**
   * Tell GTM when a user updated their payment
   */
  paymentMethodUpdated() {
    gtm.logEvent({
      event: 'payment_method_updated',
    })
  }

  /**
   * On Autosubscribe CTA viewed
   */
  autosubscribeModalViewed() {
    gtm.logEvent({ event: 'auto_sub_modal_view' })
  }

  /**
   * On Autosubscribe modal skip
   */
  autosubscribeModalSkipped() {
    gtm.logEvent({ event: 'auto_sub_skip' })
  }

  /**
   * On Autosubscribe complete
   * @param data the pubsub event object sent back when creating
   * a subscription. Also, if a 'callback' is included call that when the
   * function is complete.
   */
  autosubscribeCompleted(data: TransactionMarketingDetails) {
    gtm.logEvent({ event: 'auto_sub_payment_info_added' })
    this.logAddPaymentInfo(data)
    data.callback?.()
  }

  /**
   * Add Payment Info GA4 Event
   * @param data the pubsub event object sent back when creating
   * a subscription. Also, if a 'callback' is included call that when the
   * function is complete.
   */
  logAddPaymentInfo(data: TransactionMarketingDetails) {
    const productName = hasStates(data.states)
      ? `${data.product}_${data.states.join('_')}`
      : data.product
    const totalPrice = data.price.toString()

    const app = 'fish'
    const productSku = productSkuNames[app].find(
      (product) => product.sku === data.product
    )
    const humanReadableSku = productSku?.humanSku
    const purchaseType =
      humanReadableSku && data.membership_level
        ? this.getGaPurchaseType(humanReadableSku, data.membership_level, true)
        : ''

    // ga4 add_payment_info event
    // @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#add_payment_info
    const ga4PaymentInfo = {
      event: 'add_payment_info',
      transaction_id: data.transactionId,
      affiliation: 'onXmaps, Inc.',
      value: totalPrice,
      currency: 'USD',
      coupon: data.promo,
      purchaseType,
      items: [
        {
          item_id: productName,
          item_name: productName,
          item_brand: data.brand,
          price: totalPrice,
          quantity: 1,
        },
      ],
    }

    return gtm.logEvent(ga4PaymentInfo)
  }

  /**
   * Purchase completed event handler
   *
   * @param data - the pubsub event object sent back when creating
   * a subscription. Also, if a 'callback' is included call that when the
   * function is complete.
   */
  purchaseCompleted(data: TransactionMarketingDetails) {
    const productName = hasStates(data.states)
      ? `${data.product}_${data.states.join('_')}`
      : data.product
    const totalPrice = data.price.toString()
    const commaSeparatedStates = data.states?.join(', ')

    // Human readable SKU for analytics purposes
    const app = 'fish'
    const productSku = productSkuNames[app].find(
      (product) => product.sku === data.product
    )
    const humanReadableSku = productSku?.humanSku
    const isYearly = humanReadableSku?.includes('year')
    const purchaseType =
      humanReadableSku && data.membership_level
        ? this.getGaPurchaseType(humanReadableSku, data.membership_level)
        : ''

    this.logKochavaEvent('Purchase', {
      product_name: productName,
      price: totalPrice,
    })

    const purchaseInfo = {
      version: 'webmap',
      event: 'purchaseComplete',
      productName: productName,
      transactionValue: totalPrice,
      promoCode: data.promo || '',
      dataState: commaSeparatedStates || '',
      subscriber: data.sub,
      purchaseMethod: data.method,
      transactionId: data.transactionId,
      utm_campaign: data.utm_campaign,
      utm_medium: data.utm_medium,
      utm_source: data.utm_source,
    }

    const GAInfo = {
      event: `${humanReadableSku}_purchase`,
      productName: productName,
      transactionValue: totalPrice,
      promoCode: data.promo || '',
      dataState: commaSeparatedStates || '',
      subscriber: data.sub,
      purchaseMethod: data.method,
      purchaseType,
    }

    // GA4 Purchase
    // @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events#example_22
    // @see https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#purchase-gtm
    const ga4purchase = {
      user_id: data.sub,
      event: 'purchase',
      ecommerce: {
        transaction_id: data.transactionId,
        affiliation: 'onXmaps, Inc.',
        value: totalPrice,
        currency: 'USD',
        coupon: data.promo,
        items: [
          {
            item_id: productName,
            item_name: humanReadableSku || productName,
            item_brand: data.brand,
            price: totalPrice,
            quantity: 1,
            item_category: app,
            item_category2: data.membership_level.toLowerCase(),
            item_category3: isYearly ? 'yearly' : 'monthly',
            // Wether the purchase was AS or a standard purchase
            item_category5: 'standard',
            promotion_id: data.promotion_id,
          },
        ],
      },
    }

    const fbBrowserId = /_fbp=(fb\.1\.\d+\.\d+)/.exec(window.document.cookie)
    const fbClientId = /_fbc=(fb\.1\.\d+\.\d+)/.exec(window.document.cookie)

    // Custom flags to send up for conversions data
    const customFlags = {
      'Facebook.EventSourceUrl': window.location.href,
      'Facebook.BrowserId': fbBrowserId?.[1] ?? null,
      'Facebook.ClickId': fbClientId?.[1] ?? null,
      'SnapchatConversions.ClientDedupId': data.transactionId,
    }

    gtm.logEvent(purchaseInfo)
    // @ts-expect-error customFlags type does not allow null values
    this.logMparticleEvent('purchase_complete', purchaseInfo, customFlags)
    gtm.logEvent(GAInfo)
    gtm.logEvent(ga4purchase)
    data.callback?.()
  }

  /**
   * Log a general purchasing action
   */
  purchaseAction(
    /** Event properties */
    props: SDKEventAttrs
  ) {
    const actionProp = {
      device_width: window.innerWidth,
      device_info: browser.deviceInfo(),
      url: window.location.pathname,
      url_params: window.location.search,
      ...props,
    }

    this.logEvent('purchase_action', actionProp)

    gtm.logEvent({ ...actionProp, event: 'purchase_action' })
  }

  /**
   * Log a general redeem action
   */
  redeemAction(
    /** Event properties */
    props: SDKEventAttrs
  ) {
    this.logEvent('redeem_action', {
      device_width: window.innerWidth,
      device_info: browser.deviceInfo(),
      url: window.location.pathname,
      url_params: window.location.search,
      ...props,
    })
  }

  /**
   * markup updated event handler
   */
  markupUndoClicked(
    /** Event properties */
    props: SDKEventAttrs
  ) {
    this.logEvent('markup_undo_clicked', props)
  }

  /**
   * changed unit setting event handler
   */
  changedUnits(
    /** Event properties */
    props: SDKEventAttrs
  ) {
    this.logEvent('changed_units_web', props)
  }

  /**
   * logged out event handler
   */
  logout() {
    this.logEvent('logged_out')
  }

  /**
   * Log an event from the marketing event listener on Vue components
   */
  logVueComponentEvent(
    /** Event with name and props */
    { name, ...rest }: SDKEventAttrs & { name: string }
  ) {
    this.logEvent(name, rest)
  }

  /**
   * Tell GTM when a user has started a trial
   */
  trialStarted() {
    gtm.logEvent({
      event: `fish_trial_started`,
    })
  }

  /**
   * Log to mParticle with user ID
   */
  async accountCreated() {
    const { getUserId } = await import('@/services/identity-client')
    const userId = await getUserId()
    this.logEvent('created_account', {
      platform: 'web',
      account_id: userId,
    })
  }

  /**
   * Update collection_count user property
   *
   * @param count - Number of collections belonging to the user
   */
  updateCollectionCountUserProperty(count: number) {
    mParticle?.Identity?.getCurrentUser()?.setUserAttribute(
      'collection_count',
      count.toString()
    )
  }

  /**
   * Update waypoint_count user property
   */
  updateWaypointCountUserProperty(
    /** Number of waypoints belonging to the user */
    count: number
  ) {
    mParticle?.Identity?.getCurrentUser()?.setUserAttribute(
      'waypoint_count',
      count.toString()
    )
  }

  /**
   * Update waypoint_wind_direction_count user property
   */
  updateWaypointWindCountUserProperty(
    /** Number of waypoints with wind belonging to the user */
    count: number
  ) {
    mParticle?.Identity?.getCurrentUser()?.setUserAttribute(
      'waypoint_wind_direction_count',
      count.toString()
    )
  }

  updateMapModeUserProperty(mode: string) {
    mParticle?.Identity?.getCurrentUser()?.setUserAttribute('map_mode', mode)
  }

  /**
   * Set species user selects during hunt onboarding
   * as an user property
   */
  setSelectedSpeciesAsUserProperty(
    /** List of species selected by user */
    species: string[]
  ) {
    mParticle?.Identity?.getCurrentUser()?.setUserAttributeList(
      'selected_species',
      species
    )
  }

  /**
   * Set ride user selects during offroad onboarding
   * as an user property
   */
  setSelectedRideAsUserProperty(
    /** Selected ride by user */
    ride: string
  ) {
    mParticle?.Identity?.getCurrentUser()?.setUserAttribute(
      'selected_ride',
      ride
    )
  }

  /**
   * Mparticle will coerce booleans to strings with varying capitalization.
   * We coerce booleans to lowercase strings for predictable behavior across
   * platforms.
   */
  sanitizeMparticleEventData(data: SDKEventAttrs): mParticleEventData {
    return Object.entries(data).reduce((obj, [key, value]) => {
      if (typeof value === 'boolean') {
        return {
          ...obj,
          [key]: value.toString().toLowerCase(),
        }
      }
      return {
        ...obj,
        [key]: value,
      }
    }, {})
  }

  /**
   * Maps the unreadable product's human-sku to a purchase-type label for better reporting
   * in GA4
   * @param humanSku - Product Human readable SKU name
   * @param membershipLevel - Product membership level
   * @returns - purchase-type label
   */
  getGaPurchaseType(
    humanSku: string,
    membershipLevel: string,
    isAutoSubscribe = false
  ) {
    if (!membershipLevel || !humanSku) {
      return ''
    }

    const app = 'fish'
    let purchaseType = `purchase_${app}_${membershipLevel.toLowerCase()}`

    const isEliteYearly = humanSku === 'nationwide_year'
    if (isEliteYearly) {
      purchaseType += '_yearly'
    }

    const isEliteMonthly = humanSku === 'nationwide_month'
    if (isEliteMonthly) {
      purchaseType += '_monthly'
    }

    if (isAutoSubscribe) {
      purchaseType += '_autosubscribe'
    }

    return purchaseType
  }
}

const marketing = new Marketing()
export default marketing
